import { MAX_TREE_LEVELS } from "@/constants";
import { ITreeItem, IPanelScope } from "@/interfaces";
import { FiltersService, TreeService } from "@/services";

export const shouldShowExpandOption = (item: any): boolean => {
  return item?.has_children || item?.children?.length > 0;
};

export const parseErrorMessages = (e: unknown): string[] => {
  const messages: string[] = [];
  if (e instanceof Error) {
    const message = e.message;
    if (isValidJSONArray(message)) {
      messages.push(...JSON.parse(message));
    } else {
      messages.push(message);
    }
  } else {
    messages.push("An unknown error occurred");
  }

  return messages;
};

function isValidJSONArray(message: string): boolean {
  try {
    const parsed = JSON.parse(message);
    return Array.isArray(parsed);
  } catch {
    return false;
  }
}

// Reusable fetch callback factory for tree components with feature flag awareness
export const createTreeFetchCallback = (
  scope: IPanelScope,
  onUpdate?: () => void,
) => {
  return async (item: ITreeItem): Promise<void> => {
    try {
      const fetchedItem = await FiltersService.fetchAutoFilterChildren(
        scope,
        item,
      );

      if (fetchedItem?.children) {
        item.children = fetchedItem.children;

        // Keep the node open/expanded after fetching children
        const openedNodes = TreeService.getOpenedNodes(scope);
        if (!openedNodes.includes(item.id)) {
          openedNodes.push(item.id);
          TreeService.setOpenedNodes(scope, openedNodes);
        }

        // Trigger re-render callback if provided
        if (onUpdate) {
          onUpdate();
        }
      }
    } catch (error) {
      console.error("Failed to fetch children for tree item:", item.id, error);
      throw error; // Re-throw to allow component-specific error handling
    }
  };
};

const buildAncestryPath = (id: string): string[] => {
  const ancestries: string[] = [];

  if (id.includes("/")) {
    const parts = id.split("/");
    let currentPath = parts[0];
    ancestries.push(currentPath);

    const maxParts = Math.min(parts.length, MAX_TREE_LEVELS * 2 + 1);

    for (let i = 1; i < maxParts - 1; i += 2) {
      if (i + 1 < parts.length) {
        currentPath += `/${parts[i]}/${parts[i + 1]}`;
        ancestries.push(currentPath);
      }
    }

    // Only include the full target ID if it's within limits
    if (!ancestries.includes(id) && parts.length <= maxParts) {
      ancestries.push(id);
    }
  } else {
    ancestries.push(id);
  }

  return ancestries;
};

// Configuration for ancestry fetching
type AncestryFetchConfig = {
  maxIterations?: number;
  logErrors?: boolean;
};

const DEFAULT_ANCESTRY_CONFIG: Required<AncestryFetchConfig> = {
  maxIterations: 10,
  logErrors: true,
};

const fetchNodeAndAncestry = async (
  items: ITreeItem[],
  id: string,
  fetchChildrenCallback?: (item: ITreeItem) => Promise<void>,
  config: AncestryFetchConfig = {},
): Promise<boolean> => {
  const { maxIterations, logErrors } = {
    ...DEFAULT_ANCESTRY_CONFIG,
    ...config,
  };

  const ancestryPaths = buildAncestryPath(id);

  let foundTarget = false;
  let iteration = 0;

  while (!foundTarget && iteration < maxIterations) {
    iteration++;

    if (findNodeInTree(items, id)) {
      foundTarget = true;
      break;
    }

    const nodesToFetch = findNodesNeedingChildren(items, ancestryPaths);

    if (nodesToFetch.length === 0) {
      break;
    }

    await Promise.all(
      nodesToFetch.map(async (nodeToFetch) => {
        if (fetchChildrenCallback) {
          try {
            await fetchChildrenCallback(nodeToFetch);
          } catch (error) {
            if (logErrors) {
              console.error(
                "Failed to fetch children for ancestry:",
                nodeToFetch.id,
                error,
              );
            }
          }
        }
      }),
    );
  }

  return !!findNodeInTree(items, id);
};

const findNodesNeedingChildren = (
  items: ITreeItem[],
  ancestryPaths: string[],
): ITreeItem[] => {
  const nodesToFetch: ITreeItem[] = [];
  const processedIds = new Set<string>();

  const checkNode = (item: ITreeItem) => {
    // Check if this node is on any of the paths to our target and has unfetched children
    for (const targetPath of ancestryPaths) {
      if (
        targetPath.startsWith(item.id) &&
        item.serverObject?.has_children &&
        item.children.length === 0 &&
        !processedIds.has(item.id)
      ) {
        nodesToFetch.push(item);
        processedIds.add(item.id);
        break; // No need to check other paths for this item
      }
    }

    // Recursively check children
    if (item.children?.length) {
      for (const child of item.children) {
        checkNode(child);
      }
    }
  };

  for (const item of items) {
    checkNode(item);
  }

  return nodesToFetch;
};

const findNodeInTree = (
  items: ITreeItem[],
  id: string,
): ITreeItem | undefined => {
  for (const item of items) {
    if (item.id === id) {
      return item;
    }
    if (item.children?.length) {
      const found = findNodeInTree(item.children, id);
      if (found) {
        return found;
      }
    }
  }
  return undefined;
};

// Synchronous version for backward compatibility
export const findNodeById = (
  items: ITreeItem[],
  id: string,
): ITreeItem | undefined => {
  return findNodeInTree(items, id);
};

// Enhanced async version with automatic ancestry fetching and feature flag support
export const findNodeByIdAsync = async (
  items: ITreeItem[],
  id: string,
  fetchChildrenCallback?: (item: ITreeItem) => Promise<void>,
  config: AncestryFetchConfig = {},
): Promise<ITreeItem | undefined> => {
  // First, try to find the node in the existing tree
  let found = findNodeInTree(items, id);
  if (found) {
    return found;
  }

  // Check feature flag constraints - always respect the feature flag
  const finalConfig = { ...DEFAULT_ANCESTRY_CONFIG, ...config };

  // If not found and we have a fetch callback, attempt deep fetching
  if (fetchChildrenCallback) {
    if (id.includes("/")) {
      // Use ancestry-based fetching for complex paths
      const foundViaAncestry = await fetchNodeAndAncestry(
        items,
        id,
        fetchChildrenCallback,
        finalConfig,
      );

      if (foundViaAncestry) {
        found = findNodeInTree(items, id);
        if (found) {
          return found;
        }
      }
    } else {
      // Simple recursive fetch for non-path IDs
      found = await fetchNodeRecursively(items, id, fetchChildrenCallback);
      if (found) {
        return found;
      }
    }
  }

  return undefined;
};

// Helper function for recursive fetching (non-path IDs)
const fetchNodeRecursively = async (
  items: ITreeItem[],
  id: string,
  fetchChildrenCallback: (item: ITreeItem) => Promise<void>,
): Promise<ITreeItem | undefined> => {
  for (const item of items) {
    if (item.id === id) {
      return item;
    }

    // Check if we need to fetch children for this item
    if (
      id.startsWith(item.id) &&
      item.serverObject?.has_children &&
      item.children.length === 0
    ) {
      try {
        await fetchChildrenCallback(item);
      } catch (error) {
        console.error("Failed to fetch children for item:", item.id, error);
      }
    }

    if (item.children?.length) {
      const foundInChildren = await fetchNodeRecursively(
        item.children,
        id,
        fetchChildrenCallback,
      );
      if (foundInChildren) {
        return foundInChildren;
      }
    }
  }
  return undefined;
};

export const findNameById = (
  items: ITreeItem[],
  id: string,
): string | undefined => {
  const f = findNodeById(items, id);
  return f?.name.replace(/\s\(\d+\)$/, "");
};

export function parseUrl(url: string): {
  base: string;
  params: URLSearchParams;
} {
  const { origin, pathname, searchParams } = new URL(url);
  return {
    base: origin + pathname,
    params: searchParams,
  };
}

export function mergeUrlParams(url1: string, url2: string): string {
  const { base: base1, params: params1 } = parseUrl(url1);
  const { params: params2 } = parseUrl(url2);
  const filterParamPattern =
    /^(filter\[.*\]\[\]|instance_view_mode|dashboard_item_id)$/;

  // find matching parameters in both URLs
  const matchingFilterParams1 = Array.from(params1.keys()).filter((key) =>
    filterParamPattern.test(key),
  );
  const matchingFilterParams2 = Array.from(params2.keys()).filter((key) =>
    filterParamPattern.test(key),
  );

  const mergedParams = new URLSearchParams(params1);

  // remove matching parameters only if they exist in both URLs
  if (matchingFilterParams1.length > 0 && matchingFilterParams2.length > 0) {
    matchingFilterParams1.forEach((param) => {
      mergedParams.delete(param);
    });
    matchingFilterParams2.forEach((param) => {
      params2.delete(param);
    });
  }

  params2.forEach((value, key) => mergedParams.set(key, value));

  return `${base1}?${mergedParams.toString().replace(/%2F/g, "/")}`;
}
